import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cargoAPI } from "@/lib/api";
import { format } from "date-fns";
import { PartnerBadges, VerifiedBadgeIcon } from "@/components/PartnerBadges";
import { 
  Package, 
  Search, 
  Truck, 
  Train, 
  Plane, 
  Ship,
  MapPin,
  Calendar as CalendarIcon,
  Weight,
  IndianRupee,
  ArrowRight,
  Loader2,
  Filter
} from "lucide-react";

const transportIcons = {
  road: Truck,
  rail: Train,
  air: Plane,
  water: Ship,
};

const transportColors = {
  road: "bg-orange-500",
  rail: "bg-emerald-500",
  air: "bg-blue-500",
  water: "bg-cyan-500",
};

const indianCities = [
  "Mumbai", "Delhi", "Bangalore", "Chennai", "Kolkata", "Hyderabad", 
  "Pune", "Ahmedabad", "Jaipur", "Surat", "Lucknow", "Kanpur",
  "Nagpur", "Indore", "Thane", "Bhopal", "Visakhapatnam", "Patna",
  "Vadodara", "Ghaziabad", "Ludhiana", "Agra", "Nashik", "Faridabad",
  "Meerut", "Rajkot", "Varanasi", "Srinagar", "Aurangabad", "Dhanbad"
];

export default function BrowseListings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    transport_mode: searchParams.get("mode") || "",
    origin_city: "",
    destination_city: "",
    departure_date: null,
    min_capacity: "",
  });

  const fetchListings = useCallback(async (filterParams = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.transport_mode && filterParams.transport_mode !== "all") params.transport_mode = filterParams.transport_mode;
      if (filterParams.origin_city && filterParams.origin_city !== "any") params.origin_city = filterParams.origin_city;
      if (filterParams.destination_city && filterParams.destination_city !== "any") params.destination_city = filterParams.destination_city;
      if (filterParams.departure_date) params.departure_date = format(filterParams.departure_date, "yyyy-MM-dd");
      if (filterParams.min_capacity) params.min_capacity = parseFloat(filterParams.min_capacity);
      
      const response = await cargoAPI.getAll(params);
      setListings(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch listings:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchListings(filters);
  };

  const clearFilters = () => {
    setFilters({
      transport_mode: "",
      origin_city: "",
      destination_city: "",
      departure_date: null,
      min_capacity: "",
    });
    fetchListings({});
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/private-jets">
                <Button variant="ghost" className="rounded-none" data-testid="nav-jets-btn">
                  Private Jets
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="rounded-none btn-hover" data-testid="nav-auth-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header & Search */}
      <div className="border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-black tracking-tight mb-6">Browse Cargo Space</h1>
          
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Main Search Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label className="label-text">From</Label>
                <Select
                  value={filters.origin_city}
                  onValueChange={(value) => setFilters({ ...filters, origin_city: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="filter-origin">
                    <SelectValue placeholder="Origin City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any City</SelectItem>
                    {indianCities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">To</Label>
                <Select
                  value={filters.destination_city}
                  onValueChange={(value) => setFilters({ ...filters, destination_city: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="filter-destination">
                    <SelectValue placeholder="Destination City" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any City</SelectItem>
                    {indianCities.map((city) => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start rounded-none font-normal" data-testid="filter-date">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {filters.departure_date ? format(filters.departure_date, "PPP") : "Select Date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={filters.departure_date}
                      onSelect={(date) => setFilters({ ...filters, departure_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label className="label-text">&nbsp;</Label>
                <Button type="submit" className="w-full rounded-none btn-hover" data-testid="search-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>

            {/* Filter Toggle */}
            <Button 
              type="button" 
              variant="ghost" 
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="rounded-none"
              data-testid="toggle-filters"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? "Hide" : "Show"} More Filters
            </Button>

            {/* Additional Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-border">
                <div className="space-y-2">
                  <Label className="label-text">Transport Mode</Label>
                  <Select
                    value={filters.transport_mode}
                    onValueChange={(value) => setFilters({ ...filters, transport_mode: value })}
                  >
                    <SelectTrigger className="rounded-none" data-testid="filter-mode">
                      <SelectValue placeholder="All Modes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      <SelectItem value="road">Road</SelectItem>
                      <SelectItem value="rail">Rail</SelectItem>
                      <SelectItem value="air">Air</SelectItem>
                      <SelectItem value="water">Water</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="label-text">Min. Capacity (kg)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 100"
                    value={filters.min_capacity}
                    onChange={(e) => setFilters({ ...filters, min_capacity: e.target.value })}
                    className="rounded-none"
                    data-testid="filter-capacity"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="label-text">&nbsp;</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={clearFilters}
                    className="w-full rounded-none"
                    data-testid="clear-filters"
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-bold mb-2">No Listings Found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search filters or check back later.
            </p>
            <Button variant="outline" onClick={clearFilters} className="rounded-none" data-testid="reset-search-btn">
              Reset Search
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              Found <strong>{listings.length}</strong> available listings
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
              {listings.map((listing, idx) => {
                const Icon = transportIcons[listing.transport_mode] || Package;
                const bgColor = transportColors[listing.transport_mode] || "bg-gray-500";
                
                return (
                  <Link 
                    key={listing.id} 
                    to={`/listing/${listing.id}`}
                    className="bg-background p-6 block group card-hover opacity-0 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 0.05}s`, animationFillMode: 'forwards' }}
                    data-testid={`listing-card-${listing.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-10 h-10 ${bgColor} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="label-text">{listing.transport_mode}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-lg font-bold mb-2">
                      <span>{listing.origin_city}</span>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <span>{listing.destination_city}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm text-muted-foreground">
                        by {listing.partner_name}
                      </p>
                      {listing.partner_verified && <VerifiedBadgeIcon className="w-4 h-4" />}
                    </div>
                    
                    {listing.partner_badges?.length > 0 && (
                      <div className="mb-3">
                        <PartnerBadges badges={listing.partner_badges} size="sm" maxShow={2} />
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        <span>{listing.departure_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Weight className="w-4 h-4 text-muted-foreground" />
                        <span>{listing.available_capacity} kg</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-4 h-4" />
                        <span className="text-xl font-bold">{listing.price_per_kg}</span>
                        <span className="text-sm text-muted-foreground">/kg</span>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-none group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        View Details
                      </Button>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
